import { Stack, Text, Group, Box, UnstyledButton } from '@mantine/core';
import classes from './AgentsModal.module.css';
import { IconCheck } from '@tabler/icons-react';
import { useChatStore } from '../../../../stores/chatStore';
import { trackComponentInteraction } from '../../../../utils/analytics';
import { useAgents } from '../../../../api/agents';
import { Agent } from '../../../../api/base/types';

interface AgentsModalProps {
  onAgentSelect?: (agent: Agent) => void;
  onAgentRemove?: (agentId: string) => void;
  selectedAgents?: string[];
}

export const AgentsModal = ({ onAgentSelect, onAgentRemove, selectedAgents = [] }: AgentsModalProps) => {
  // Get Genie access state from chat store
  const hasGenieAccess = useChatStore(state => state.hasGenieAccess);
  
  // Fetch agents from API
  const { data: agents = [], isLoading } = useAgents();

  // Filter agents based on Genie access
  const filteredAgents = agents.filter(agent => agent.id !== 'genie' || hasGenieAccess);

  const handleAgentClick = (agent: Agent) => {
    // Track the agent interaction
    trackComponentInteraction(
      'AgentsModal',
      agent.display_name,
      'agent',
      'click',
      'agent_interaction'
    );

    // Track Code Interpreter specific selection
    if (agent.id === 'codeinterpreter') {
      trackComponentInteraction(
        'AgentSelector',
        'Code Interpreter',
        'button',
        'click',
        'agent_select_code_interpreter'
      );
    }

    // If agent is already selected, remove it; otherwise add it
    if (selectedAgents.includes(agent.id)) {
      onAgentRemove?.(agent.id);
    } else {
      onAgentSelect?.(agent);
    }
  };

  return (
    <Box className={classes.modal}>
      <Box className={classes.sectionHeader}>
        <Text className={classes.sectionTitle}>Select Agent</Text>
      </Box>

      {isLoading ? (
        <Box p="md">
          <Text size="sm" c="dimmed">Loading agents...</Text>
        </Box>
      ) : (
        <Stack gap={0} className={classes.agentList}>
          {filteredAgents.map((agent) => {
            const isSelected = selectedAgents.includes(agent.id);
            return (
              <UnstyledButton
                key={agent.id}
                className={`${classes.agentItem} ${isSelected ? classes.selected : ''}`}
                onClick={() => handleAgentClick(agent)}
              >
                <Group gap={9} align="flex-start">
                  <Box className={classes.agentContent}>
                    <Text className={classes.agentName}>{agent.display_name}</Text>
                    <Text className={classes.agentDescription}>{agent.description}</Text>
                  </Box>
                  {isSelected && (
                    <Box className={classes.checkIcon}>
                      <IconCheck size={20} color="var(--fonterra-border-accent)" />
                    </Box>
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export type { AgentsModalProps };
